import React, { useState } from "react";
import DSBox from "../../../system-design/DSBox";
import DSCard from "../../../system-design/DSCard";
import DSCardContent from "../../../system-design/DSCardContent";
import DSButton from "../../../system-design/DSButton";
import DSErrorButton from "../../../system-design/DSErrorButton";
import { DSBaseTable, DSTableCell } from "../../../system-design/Table";
import { Column } from "../../../system-design/Table/DSBaseTable/components/DSBaseTable";
import ConfirmationModal from "../../../ConfirmationModal";
import { useDevice } from "../../context/DeviceModalContext";
import useGetKlydosByIds from "../../hooks/useGetKlydosByIds";
import { Klydo } from "../../../../api/klydo";
import KlydoModal from "../../../Klydos/components/KlydoModal";
import DSDialog from "../../../system-design/DSDialog";
import KlydoLoopUrl from "../../../../components/klydoPreview/KlydoLoopUrl";
import { InsertTarget, PendingKlydo } from "../../../../Types";
import AddPendingKlydoDialog from "./AddPendingKlydoDialog";
import { useQueries } from "@tanstack/react-query";
import collectionApiClient from "../../../../api/collection";
import firebaseService from "../../../../firebase_service/firebaseService";
import { extractApiErrorMessage } from "../../../../utils/util";
import { validatePendingKlydoIds } from "../../../../utils/validatePendingKlydos";
import DSSectionHeading from "../../../system-design/DSSectionHeading";
import { sectionCardSx } from "../../../system-design/sectionCardSx";

type PendingKlydoRow = Klydo & {
  klydoId: string;
  insertTo: InsertTarget[];
  ttl: number | null;
  playNow: boolean;
  attempts: number;
  forceDownload: boolean;
};

const DevicePendingKlydosCard = () => {
  const { device, updateConfig, refetch } = useDevice();
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [showClearConfirmation, setShowClearConfirmation] = useState(false);
  const [selectedKlydo, setSelectedKlydo] = useState<{ id: string } | null>(
    null,
  );
  const [deletingKlydoId, setDeletingKlydoId] = useState<string | null>(null);

  const pendingKlydos: PendingKlydo[] = device.pendingKlydos?.value ?? [];
  // Mirrors the clock's own draft-visibility gate: dev = hideDev explicitly
  // false, plus the artist and rater flags.
  const draftsAllowed =
    device.hideDev?.value === false ||
    device.isArtist?.value === true ||
    device.isKlydosRater?.value === true;
  const klydoIds = pendingKlydos.map((pk) => pk.id);
  const { klydos, isLoading, error } = useGetKlydosByIds(klydoIds);

  const collectionIds = Array.from(
    new Set(
      pendingKlydos.flatMap((pk) =>
        (pk.insertTo ?? [])
          .filter((t) => t.mode === "COLLECTIONS" && t.target)
          .map((t) => t.target as string),
      ),
    ),
  );
  const collectionQueries = useQueries({
    queries: collectionIds.map((id) => ({
      queryKey: ["collection", id],
      queryFn: () => collectionApiClient.getCollectionById(id),
      staleTime: Infinity,
      refetchOnWindowFocus: false,
    })),
  });
  const collectionNameMap = new Map(
    collectionIds.map((id, i) => [id, collectionQueries[i]?.data?.name ?? id]),
  );

  const rows: PendingKlydoRow[] = pendingKlydos.map((pk) => {
    const klydo = klydos.find((k) => k.id === pk.id);
    return {
      ...((klydo || {}) as Klydo),
      klydoId: pk.id,
      insertTo: pk.insertTo,
      ttl: pk.ttl,
      playNow: pk.playNow,
      attempts: pk.attempts,
      forceDownload: pk.forceDownload ?? false,
    };
  });

  const columns: Column<PendingKlydoRow>[] = [
    { field: "loopUrl", label: "Klydo", sortable: false },
    { field: "name", label: "Name", sortable: false },
    { field: "insertTo", label: "Insert To", sortable: false },
    { field: "ttl", label: "TTL", sortable: false },
    { field: "playNow", label: "Play Now", sortable: false },
    { field: "forceDownload", label: "Force DL", sortable: false },
    { field: "attempts", label: "Attempts", sortable: false },
  ];

  const handleKlydoClick = (row: PendingKlydoRow) => {
    if (row.klydoId) {
      setSelectedKlydo({ id: row.klydoId });
    }
  };

  const rowBuilder = (row: PendingKlydoRow) => (
    <>
      <DSTableCell
        key="klydo"
        onClick={() => handleKlydoClick(row)}
        sx={{ cursor: "pointer" }}
      >
        <DSBox sx={{ width: 35, height: 35, overflow: "hidden" }}>
          <KlydoLoopUrl loopUrl={row?.loopUrl || ""} />
        </DSBox>
      </DSTableCell>
      <DSTableCell key="name">
        {row?.name || row?.creator || "Unknown"}
      </DSTableCell>
      <DSTableCell key="insertTo">
        {(row.insertTo ?? [])
          .map((t) =>
            (t.mode === "COLLECTIONS" || t.mode === "EXPLORE") && t.target
              ? `${t.mode} (${t.mode === "COLLECTIONS" ? (collectionNameMap.get(t.target) ?? t.target) : t.target})`
              : t.mode,
          )
          .join(", ")}
      </DSTableCell>
      <DSTableCell key="ttl">
        {row.ttl != null ? `${row.ttl}m` : "-"}
      </DSTableCell>
      <DSTableCell key="playNow">{row.playNow ? "Yes" : "No"}</DSTableCell>
      <DSTableCell key="forceDownload">
        {row.forceDownload ? "Yes" : "No"}
      </DSTableCell>
      <DSTableCell key="attempts">{row.attempts}</DSTableCell>
    </>
  );

  const handleRemovePending = async (row: PendingKlydoRow) => {
    setDeletingKlydoId(row.klydoId);
    try {
      // Removes just this id off a FRESH read, rather than writing back a
      // filtered copy of the render-time snapshot — that would also drop
      // anything the clock or the backend appended since the page loaded.
      await firebaseService.removePendingKlydoFromDevice(
        device.id,
        row.klydoId,
      );
      refetch();
    } finally {
      setDeletingKlydoId(null);
    }
  };

  // The sealed-only rule is enforced HERE, in the UI (deliberate — there is
  // no server-side check on this write): validate the selection before the
  // direct Firestore write.
  const handleAddPendingKlydo = async (newPendingList: PendingKlydo[]) => {
    const alreadyPendingIds = newPendingList
      .map((pk) => pk.id)
      .filter((id) => pendingKlydos.some((existing) => existing.id === id));
    if (alreadyPendingIds.length) {
      setAddError(
        `Already pending on this clock — remove first to re-add: ` +
          alreadyPendingIds.join(", "),
      );
      return;
    }
    setAddError(null);
    setIsAdding(true);
    try {
      const validationError = await validatePendingKlydoIds(
        newPendingList.map((pk) => pk.id),
        draftsAllowed,
      );
      if (validationError) {
        setAddError(validationError);
        return;
      }
      // Read-modify-write against the LIVE doc rather than
      // `updateConfig("pendingKlydos", [...pendingKlydos, ...new])`. The array
      // above is a render-time snapshot, and the validation fetch just added a
      // round-trip between it and the write — a whole-array write off a stale
      // snapshot drops anything the clock or another admin appended meanwhile
      // (a gift was lost in production exactly this way). The helper re-reads
      // immediately before writing and skips ids already pending, so it is
      // also safe to retry.
      const { failed } = await firebaseService.addPendingKlydosToDevices(
        [device.id],
        newPendingList,
      );
      if (failed.length) {
        setAddError("Failed to add pending klydos — you can safely retry.");
        return;
      }
      refetch();
      setShowAddDialog(false);
    } catch (error) {
      setAddError(
        extractApiErrorMessage(error, "Failed to add pending klydos"),
      );
    } finally {
      setIsAdding(false);
    }
  };

  // Deliberately a whole-array reset, unlike Add and Remove above: emptying the
  // queue IS the operation, so an entry that arrived since the render is meant
  // to go too. Kept as `updateConfig` for that reason, not by oversight.
  const handleClearAll = async () => {
    await updateConfig("pendingKlydos", []);
    refetch();
    setShowClearConfirmation(false);
  };

  return (
    <DSBox sx={{ mb: 2 }}>
      <DSCard sx={sectionCardSx}>
        <DSSectionHeading>
          {`Pending Klydos ${pendingKlydos.length > 0 ? `(${pendingKlydos.length})` : ""}`}
        </DSSectionHeading>

        <DSCardContent>
          {error ? (
            <DSBox
              sx={{ textAlign: "center", padding: "20px", color: "error.main" }}
            >
              Failed to load pending klydos
            </DSBox>
          ) : isLoading && klydoIds.length > 0 ? (
            <DSBox sx={{ textAlign: "center", padding: "20px" }}>
              Loading pending klydos...
            </DSBox>
          ) : pendingKlydos.length > 0 ? (
            <DSBaseTable
              data={rows}
              columns={columns}
              rowBuilder={rowBuilder}
              onPressDelete={handleRemovePending}
              defaultRowsPerPage={5}
              deletingRowId={deletingKlydoId}
              getRowId={(row) => row.klydoId}
            />
          ) : (
            <DSBox sx={{ textAlign: "center", padding: "20px" }}>
              No pending klydos
            </DSBox>
          )}

          <DSBox sx={{ display: "flex", gap: "10px", marginTop: "15px" }}>
            <DSButton onClick={() => setShowAddDialog(true)}>
              Add Pending Klydo
            </DSButton>
            {pendingKlydos.length > 0 && (
              <DSErrorButton
                onClick={() => setShowClearConfirmation(true)}
                sx={{ minWidth: "120px" }}
              >
                Clear All
              </DSErrorButton>
            )}
          </DSBox>

          {showAddDialog && (
            <AddPendingKlydoDialog
              open={showAddDialog}
              onClose={() => {
                setAddError(null);
                setShowAddDialog(false);
              }}
              onAdd={handleAddPendingKlydo}
              existingCollections={device.machineCollections?.value ?? []}
              draftsAllowed={draftsAllowed}
              errorMessage={addError}
              isSaving={isAdding}
            />
          )}

          <ConfirmationModal
            open={showClearConfirmation}
            message={`Are you sure you want to clear all ${pendingKlydos.length} pending klydo(s)? This cannot be undone.`}
            onClose={() => setShowClearConfirmation(false)}
            onConfirm={handleClearAll}
          />

          {selectedKlydo && (
            <DSDialog
              fullWidth
              maxWidth="lg"
              open={!!selectedKlydo}
              onClose={() => setSelectedKlydo(null)}
            >
              <KlydoModal
                klydoId={selectedKlydo.id}
                setShowModal={() => setSelectedKlydo(null)}
              />
            </DSDialog>
          )}
        </DSCardContent>
      </DSCard>
    </DSBox>
  );
};

export default DevicePendingKlydosCard;
